import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import type { ChatMessage, ChatNote, MessageClickHandler } from './chat-message.interface'
import { ChatMessageAttachmentList } from './chat-message-attachment-list.molecule'
import { ChatMessageHeader } from './chat-message-header.atom'
import type { BubbleVariant } from './chat-message-item.helper'
import { ChatMessageRowActions } from './chat-message-row-actions.atom'

interface ChatMessageBodyProps {
  message: ChatMessage | ChatNote
  senderName: string
  bubbleVariant: BubbleVariant
  isNote: boolean
  isRightAligned: boolean
  isPinned: boolean
  isHighlighted?: boolean
  onPin?: (itemId: string) => void
  onClick?: MessageClickHandler
  onAttachmentDownload?: (attachmentId: string) => void
}

/**
 * Body content of a chat message including header, content, and actions.
 */
export const ChatMessageBody: FC<ChatMessageBodyProps> = ({
  message,
  senderName,
  bubbleVariant,
  isNote,
  isRightAligned,
  isPinned,
  isHighlighted = false,
  onPin,
  onClick,
  onAttachmentDownload,
}): ReactNode => {
  const hasAttachments = message.type === 'message' && !!message.attachmentList?.length

  return (
    <div
      className={cn(
        'flex min-w-0 max-w-[75%] flex-col gap-1',
        isRightAligned ? 'items-end' : 'items-start',
        isHighlighted && 'chat-pin-flash'
      )}
    >
      <ChatMessageHeader
        senderName={senderName}
        timestamp={message.timestamp}
        isNote={isNote}
        isRightAligned={isRightAligned}
      />
      {hasAttachments && message.type === 'message' && message.attachmentList ? (
        <ChatMessageAttachmentList
          attachmentList={message.attachmentList}
          onDownload={onAttachmentDownload}
        />
      ) : null}
      <ChatMessageRowActions
        message={message}
        bubbleVariant={bubbleVariant}
        isRightAligned={isRightAligned}
        isPinned={isPinned}
        onPin={onPin}
        onClick={onClick}
      />
    </div>
  )
}

ChatMessageBody.displayName = 'ChatMessageBody'
